import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import { PurchaseRequisition, PRFormData } from '@/types/procurement';
import { notifyProcurement } from '@/lib/notifications/notificationService';
import { TEMPLATE_PR_APPROVED, TEMPLATE_PR_REJECTED } from '@/lib/notifications/templateCodes';

interface PRState {
  prs: PurchaseRequisition[];
  isLoading: boolean;
  error: string | null;
  fetchPRs: () => Promise<void>;
  createPR: (data: PRFormData) => Promise<void>;
  updatePR: (id: string, data: Partial<PRFormData>) => Promise<void>;
  deletePR: (id: string) => Promise<void>;
  approvePR: (id: string) => Promise<void>;
  rejectPR: (id: string, reason: string) => Promise<void>;
  cancelPR: (id: string) => Promise<void>;
}

export const usePRStore = create<PRState>((set, get) => ({
  prs: [],
  isLoading: false,
  error: null,

  fetchPRs: async () => {
    try {
      set({ isLoading: true, error: null });
      const { data, error } = await supabase
        .from('purchase_requisition')
        .select(`
          *,
          items:pr_line(
            *,
            item:inventory_items(id, code, name, category, unit),
            preferred_supplier:suppliers(id, name, code)
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ prs: data || [] });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch PRs';
      set({ error: message });
    } finally {
      set({ isLoading: false });
    }
  },

  createPR: async (data) => {
    try {
      set({ isLoading: true, error: null });

      // First create the PR
      const { data: pr, error: prError } = await supabase
        .from('purchase_requisition')
        .insert([{
          status: 'DRAFT',
          needed_by: data.needed_by,
          remarks: data.remarks,
          requester_id: (await supabase.auth.getUser()).data.user?.id
        }])
        .select()
        .single();

      if (prError) throw prError;

      // Then create PR line items
      if (data.lines?.length) {
        const prLines = data.lines.map((line) => ({
          item_id: line.item_id,
          qty: line.qty,
          uom: line.uom,
          preferred_supplier_id: line.preferred_supplier_id,
          pr_id: pr.id
        }));

        const { error: linesError } = await supabase
          .from('pr_line')
          .insert(prLines);

        if (linesError) throw linesError;
      }

      // Refresh PRs list
      await get().fetchPRs();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create PR';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  updatePR: async (id, data) => {
    try {
      set({ isLoading: true, error: null });

      // Update PR
      const { error: prError } = await supabase
        .from('purchase_requisition')
        .update({
          needed_by: data.needed_by,
          remarks: data.remarks,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (prError) throw prError;

      // Update lines if provided
      if (data.lines) {
        // First delete existing lines
        const { error: deleteError } = await supabase
          .from('pr_line')
          .delete()
          .eq('pr_id', id);

        if (deleteError) throw deleteError;

        // Then insert new lines
        const prLines = data.lines.map((line) => ({
          item_id: line.item_id,
          qty: line.qty,
          uom: line.uom,
          preferred_supplier_id: line.preferred_supplier_id,
          pr_id: id
        }));

        const { error: linesError } = await supabase
          .from('pr_line')
          .insert(prLines);

        if (linesError) throw linesError;
      }

      // Refresh PRs list
      await get().fetchPRs();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update PR';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  deletePR: async (id) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('purchase_requisition')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await get().fetchPRs();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete PR';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  approvePR: async (id) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('purchase_requisition')
        .update({ 
          status: 'approved',
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      // Queue email notification for PR approval
      const pr = get().prs.find(p => p.id === id);
      const user = (await supabase.auth.getUser()).data.user;
      notifyProcurement(
        TEMPLATE_PR_APPROVED,
        {
          pr_code: pr?.code || id,
          approved_by: user?.email || 'System',
          items: pr?.pr_lines?.length ? `${pr.pr_lines.length} item(s)` : 'N/A',
        },
        'purchase_requisition',
        id
      );

      await get().fetchPRs();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to approve PR';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  rejectPR: async (id, reason) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('purchase_requisition')
        .update({ 
          status: 'rejected',
          notes: reason,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      // Queue email notification for PR rejection
      const pr = get().prs.find(p => p.id === id);
      const user = (await supabase.auth.getUser()).data.user;
      notifyProcurement(
        TEMPLATE_PR_REJECTED,
        {
          pr_code: pr?.code || id,
          rejected_by: user?.email || 'System',
          reason: reason,
        },
        'purchase_requisition',
        id,
        'HIGH'
      );

      await get().fetchPRs();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reject PR';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  cancelPR: async (id) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('purchase_requisition')
        .update({ 
          status: 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;
      await get().fetchPRs();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to cancel PR';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  }
}));

// Set up realtime subscription
supabase
  .channel('pr_changes')
  .on(
    'postgres_changes',
    { event: '*', schema: 'public', table: 'purchase_requisition' },
    () => {
      usePRStore.getState().fetchPRs();
    }
  )
  .subscribe();